import { QdzElement, define, motion } from '@qdz/element';
import { QdzCarousel } from '@qdz/carousel';

const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

const setOrigin = (element, event) => {
  const rect = element.getBoundingClientRect();
  element.style.setProperty('--qdz-zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
  element.style.setProperty('--qdz-zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
};

export class QdzMediaGallery extends QdzCarousel {
  get slides() {
    return super.slides.filter((slide) => !slide.hidden);
  }

  mount() {
    super.mount();
    this.listen(this, 'qdz:carousel:change', (event) => this.#sync(event.detail.index));
    for (const thumb of this.refs('thumb')) {
      this.listen(thumb, 'click', () => this.showMedia(thumb.dataset.mediaId));
    }
    for (const button of this.refs('zoom')) {
      this.listen(button, 'click', () => this.#openLightbox(button.closest('[data-media-id]').dataset.mediaId, button));
    }
    const open = this.ref('open');
    if (open) this.listen(open, 'click', () => this.#openLightbox(this.slides[this.index]?.dataset.mediaId, open));
    if (this.hasAttribute('hover-zoom')) this.#hoverZoom();
    this.#thumbArrows();
    const root = this.closest('[data-qdz-product]');
    if (root) {
      this.listen(root, 'qdz:variant:change', (event) => {
        if (this.hasAttribute('color-filter')) this.filterColor(event.detail.color ?? '');
        if (event.detail.mediaId) this.showMedia(event.detail.mediaId, { fromVariant: true });
      });
      this.listen(root, 'qdz:swatch:preview', (event) => this.preview(event.detail.srcset));
    }
  }

  get scrollable() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  }

  preview(srcset) {
    if (srcset) {
      const image = (this.slides[this.index] ?? this.slides[0])?.querySelector('img');
      if (!image) return;
      if (this.previewing && this.previewing.image !== image) this.preview(null);
      this.previewing ??= { image, srcset: image.getAttribute('srcset'), src: image.getAttribute('src') };
      image.srcset = srcset;
      image.src = srcset.split(' ')[0];
      return;
    }
    if (!this.previewing) return;
    const { image, srcset: original, src } = this.previewing;
    if (original === null) image.removeAttribute('srcset');
    else image.srcset = original;
    if (src) image.src = src;
    this.previewing = null;
  }

  showMedia(mediaId, { fromVariant = false } = {}) {
    const slide = this.slides.find((element) => element.dataset.mediaId === String(mediaId));
    if (!slide) return;
    if (this.scrollable) {
      this.show(this.slides.indexOf(slide));
    } else if (fromVariant && slide !== this.slides[0]) {
      this.track.prepend(slide);
    }
    this.#markThumb(slide);
  }

  filterColor(color) {
    if (!this.track) return;
    const all = [...this.track.children];
    const matches = color !== '' && all.some((slide) => slide.dataset.color === color);
    const hidden = new Set();
    for (const slide of all) {
      const hide = matches && slide.dataset.color !== undefined && slide.dataset.color !== color;
      slide.hidden = hide;
      if (hide) hidden.add(slide.dataset.mediaId);
    }
    for (const item of this.querySelectorAll('.qdz-media-gallery__thumbs > li')) {
      item.hidden = hidden.has(item.dataset.mediaId);
    }
    const dots = this.ref('dots');
    if (dots) dots.replaceChildren(...this.slides.map(() => document.createElement('span')));
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.#sync(0);
  }

  #sync(index) {
    const slides = this.slides;
    const active = slides[index];
    if (!active) return;
    const counter = this.ref('counter');
    if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
    const dots = this.ref('dots');
    if (dots) [...dots.children].forEach((dot, position) => dot.classList.toggle('is-active', position === index));
    const open = this.ref('open');
    if (open) open.hidden = !active.classList.contains('qdz-media-gallery__slide--image');
    this.#markThumb(active);
    this.#pauseExcept(active);
  }

  #markThumb(active) {
    for (const thumb of this.refs('thumb')) {
      if (thumb.dataset.mediaId === active.dataset.mediaId) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb.closest('li'));
      } else thumb.removeAttribute('aria-current');
    }
  }

  get #vertical() {
    const list = this.ref('thumbs');
    return Boolean(list) && getComputedStyle(list).flexDirection === 'column';
  }

  #revealThumb(item) {
    const list = this.ref('thumbs');
    if (!list || !item) return;
    const vertical = this.#vertical;
    const start = vertical ? item.offsetTop : item.offsetLeft;
    const size = vertical ? item.offsetHeight : item.offsetWidth;
    const view = vertical ? list.clientHeight : list.clientWidth;
    const scroll = vertical ? list.scrollTop : list.scrollLeft;
    let target = null;
    if (start < scroll) target = start;
    else if (start + size > scroll + view) target = start + size - view;
    if (target !== null) list.scrollTo({ [vertical ? 'top' : 'left']: target, behavior: motion.scroll() });
  }

  #thumbArrows() {
    const list = this.ref('thumbs');
    const previous = this.ref('thumbs-previous');
    const next = this.ref('thumbs-next');
    if (!list || !previous || !next) return;
    const update = () => {
      const vertical = this.#vertical;
      const max = vertical ? list.scrollHeight - list.clientHeight : list.scrollWidth - list.clientWidth;
      const position = Math.abs(vertical ? list.scrollTop : list.scrollLeft);
      previous.hidden = max <= 1 || position <= 1;
      next.hidden = max <= 1 || position >= max - 1;
    };
    const step = (direction) => {
      const vertical = this.#vertical;
      const distance = (vertical ? list.clientHeight : list.clientWidth) * 0.8 * direction;
      list.scrollBy({ [vertical ? 'top' : 'left']: distance, behavior: motion.scroll() });
    };
    this.listen(previous, 'click', () => step(-1));
    this.listen(next, 'click', () => step(1));
    this.listen(list, 'scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(list);
    this.signal?.addEventListener('abort', () => observer.disconnect());
    update();
  }

  #pauseExcept(active) {
    for (const slide of super.slides) {
      if (slide === active) continue;
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((frame) => {
        const message = frame.src.includes('vimeo')
          ? { method: 'pause' }
          : { event: 'command', func: 'pauseVideo', args: '' };
        frame.contentWindow?.postMessage(JSON.stringify(message), '*');
      });
    }
  }

  #hoverZoom() {
    for (const media of this.querySelectorAll('.qdz-media-gallery__slide--image .qdz-media-gallery__zoom')) {
      this.listen(media, 'pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !FINE_POINTER.matches) return;
        setOrigin(media, event);
        media.classList.add('is-zoomed');
      });
      this.listen(media, 'pointerleave', () => media.classList.remove('is-zoomed'));
    }
  }

  #openLightbox(mediaId, opener) {
    const dialog = document.getElementById(this.getAttribute('lightbox-id'));
    const lightbox = dialog?.querySelector('qdz-lightbox');
    if (!lightbox || !mediaId) return;
    lightbox.scope(this.slides.map((slide) => slide.dataset.mediaId));
    lightbox.select(mediaId);
    dialog.open(opener);
  }
}

export class QdzLightbox extends QdzElement {
  #index = 0;

  get dialog() {
    return this.closest('dialog');
  }

  get items() {
    return this.refs('item').filter((item) => !item.hasAttribute('data-excluded'));
  }

  get zoomButton() {
    return this.dialog?.querySelector('[data-lightbox-zoom]');
  }

  mount() {
    const previous = this.ref('previous');
    const next = this.ref('next');
    if (previous) this.listen(previous, 'click', () => this.go(-1));
    if (next) this.listen(next, 'click', () => this.go(1));
    for (const thumb of this.refs('thumb')) {
      this.listen(thumb, 'click', () => this.select(thumb.dataset.mediaId));
    }
    for (const frame of this.refs('frame')) {
      this.listen(frame, 'click', (event) => {
        setOrigin(frame, event);
        this.#zoom(!frame.classList.contains('is-zoomed'));
      });
      this.listen(frame, 'pointermove', (event) => {
        if (event.pointerType === 'mouse' && frame.classList.contains('is-zoomed')) setOrigin(frame, event);
      });
    }
    if (this.zoomButton) {
      this.listen(this.zoomButton, 'click', () => this.#zoom(this.zoomButton.getAttribute('aria-pressed') !== 'true'));
    }
    if (this.dialog) {
      this.listen(this.dialog, 'keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const forward = (event.key === 'ArrowRight') !== (getComputedStyle(this).direction === 'rtl');
        event.preventDefault();
        this.go(forward ? 1 : -1);
      });
    }
  }

  scope(mediaIds) {
    const ids = new Set(mediaIds.map(String));
    for (const item of this.refs('item')) item.toggleAttribute('data-excluded', !ids.has(item.dataset.mediaId));
    for (const thumb of this.refs('thumb')) thumb.closest('li').hidden = !ids.has(thumb.dataset.mediaId);
    const single = this.items.length < 2;
    for (const control of [this.ref('previous'), this.ref('next')]) {
      if (control) control.hidden = single;
    }
  }

  select(mediaId) {
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    this.#show(index < 0 ? 0 : index);
  }

  go(step) {
    const count = this.items.length;
    if (count > 1) this.#show((this.#index + step + count) % count);
  }

  #show(index) {
    const items = this.items;
    this.#index = index;
    for (const item of this.refs('item')) item.hidden = item !== items[index];
    const active = items[index];
    const counter = this.dialog?.querySelector('[data-lightbox-counter]');
    if (counter) counter.textContent = `${index + 1} / ${items.length}`;
    for (const thumb of this.refs('thumb')) {
      if (thumb.dataset.mediaId === active?.dataset.mediaId) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    this.#zoom(false);
  }

  #zoom(on) {
    for (const frame of this.refs('frame')) frame.classList.toggle('is-zoomed', on && !frame.closest('[hidden]'));
    this.zoomButton?.setAttribute('aria-pressed', String(on));
  }
}

define('qdz-media-gallery', QdzMediaGallery);
define('qdz-lightbox', QdzLightbox);
